import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import type { ClassFormat, Gym } from "../../api/hooks";
import { FormatsManager } from "../../components/admin/FormatsManager";
import { LoadingSection } from "../../components/Spinner";

type AdminSlot = {
  id: string;
  gymId: string;
  formatId: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  zone: number;
  isClosure: boolean;
  format: ClassFormat | null;
  gym: Gym;
};

type SlotDraft = {
  formatId?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  zone: number;
  isClosure: boolean;
};

const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const ZONES = [1, 2, 3];
const CLOSURE = "__closure__";

const EMPTY_DRAFT: SlotDraft = {
  dayOfWeek: 1,
  startTime: "06:30",
  endTime: "07:30",
  zone: 1,
  isClosure: false,
};

export function AdminSchedule() {
  const qc = useQueryClient();
  const { data: gyms } = useQuery({
    queryKey: ["admin", "gyms"],
    queryFn: () => api<Gym[]>("/admin/gyms"),
  });
  const { data: formats } = useQuery({
    queryKey: ["admin", "formats"],
    queryFn: () => api<ClassFormat[]>("/admin/formats"),
  });
  const [gymId, setGymId] = useState<string | null>(null);
  const current = gymId ?? gyms?.[0]?.id ?? null;

  const { data: slots, isLoading: slotsLoading } = useQuery({
    queryKey: ["admin", "schedule", current],
    queryFn: () => api<AdminSlot[]>(`/admin/schedule?gymId=${current}`),
    enabled: !!current,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SlotDraft>(EMPTY_DRAFT);
  const isEditing = editingId !== null;

  const resetForm = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const startEdit = (slot: AdminSlot) => {
    setEditingId(slot.id);
    setDraft({
      formatId: slot.formatId ?? undefined,
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
      zone: slot.zone ?? 1,
      isClosure: slot.isClosure ?? false,
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        gymId: current,
        formatId: draft.isClosure ? null : draft.formatId,
        dayOfWeek: draft.dayOfWeek,
        startTime: draft.startTime,
        endTime: draft.endTime,
        zone: draft.zone,
        isClosure: draft.isClosure,
      };
      return editingId
        ? api(`/admin/schedule/${editingId}`, { method: "PUT", json: body })
        : api("/admin/schedule", { method: "POST", json: body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
      resetForm();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/schedule/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
      if (editingId === id) resetForm();
    },
  });

  const handleDelete = (slot: AdminSlot) => {
    const ok = window.confirm(
      `Supprimer le créneau ${DAYS[slot.dayOfWeek]} ${slot.startTime}–${slot.endTime} (${slot.isClosure ? "Fermeture" : slot.format?.name}) ?`,
    );
    if (ok) remove.mutate(slot.id);
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase">Planning</h1>

      <div className="mt-4 flex gap-2">
        {gyms?.map((g) => (
          <button
            key={g.id}
            onClick={() => {
              setGymId(g.id);
              resetForm();
            }}
            className={`rounded px-3 py-2 text-sm font-semibold uppercase ${
              g.id === current ? "bg-brand-500 text-white" : "bg-white"
            }`}
          >
            {g.name}
          </button>
        ))}
      </div>

      <div className="card mt-6">
        <h2 className="font-display text-lg font-bold">
          {isEditing ? "Modifier le créneau" : "Ajouter un créneau"}
        </h2>
        <div className="mt-3 grid gap-3 md:grid-cols-6">
          <select
            className="input"
            value={draft.isClosure ? CLOSURE : draft.formatId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === CLOSURE) setDraft({ ...draft, isClosure: true, formatId: undefined });
              else setDraft({ ...draft, isClosure: false, formatId: v });
            }}
          >
            <option value="">— Format —</option>
            {formats?.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
            <option value={CLOSURE}>🔒 Fermeture</option>
          </select>
          <select
            className="input"
            value={draft.dayOfWeek}
            onChange={(e) => setDraft({ ...draft, dayOfWeek: Number(e.target.value) })}
          >
            {DAYS.map((d, i) => (
              <option key={i} value={i}>
                {d}
              </option>
            ))}
          </select>
          <select
            className="input"
            value={draft.zone}
            onChange={(e) => setDraft({ ...draft, zone: Number(e.target.value) })}
            title="Zone / espace d'entraînement"
            disabled={draft.isClosure}
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                Zone {z}
              </option>
            ))}
          </select>
          <input
            className="input"
            type="time"
            value={draft.startTime}
            onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
          />
          <input
            className="input"
            type="time"
            value={draft.endTime}
            onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
          />
          <div className="flex gap-2">
            <button
              onClick={() => save.mutate()}
              className="btn-primary flex-1"
              disabled={(!draft.isClosure && !draft.formatId) || save.isPending}
            >
              {save.isPending ? "…" : isEditing ? "Enregistrer" : "Ajouter"}
            </button>
            {isEditing && (
              <button onClick={resetForm} className="btn-outline" type="button">
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>

      {slotsLoading && <LoadingSection />}

      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-ink-300">
              {DAYS.map((d) => (
                <th key={d} className="px-2 py-2 text-left font-display uppercase">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {DAYS.map((_, idx) => (
                <td key={idx} className="align-top p-2">
                  <div className="flex flex-col gap-2">
                    {slots
                      ?.filter((s) => s.dayOfWeek === idx)
                      .map((s) => {
                        const isCurrent = editingId === s.id;
                        return (
                          <div
                            key={s.id}
                            className={`rounded border p-2 text-xs ${
                              isCurrent ? "border-brand-500 bg-brand-50" : "border-transparent bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-1 font-bold">
                              <span>
                                {s.startTime}–{s.endTime}
                              </span>
                              {!s.isClosure && (
                                <span className="rounded bg-ink-100 px-1 text-[9px] font-semibold uppercase text-ink-500">
                                  Z{s.zone ?? 1}
                                </span>
                              )}
                            </div>
                            <div className={s.isClosure ? "italic text-ink-400" : ""}>
                              {s.isClosure ? "Fermé" : s.format?.name}
                            </div>
                            <div className="mt-1 flex gap-2">
                              <button
                                onClick={() => startEdit(s)}
                                className="text-[10px] font-semibold text-ink-700 hover:text-brand-500"
                              >
                                modifier
                              </button>
                              <button
                                onClick={() => handleDelete(s)}
                                className="text-[10px] font-semibold text-brand-500 hover:underline"
                              >
                                supprimer
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <FormatsManager />
    </div>
  );
}
